<template>
  <div class="flex flex-col gap-5">
    <b-text-field v-model="title" :label="t('modal_create_experience.title')" data-e2e="experience-title-input" />
    <b-text-field v-model="pipelineRevenue" type="number" class="group" :label="t('modal_create_experience.pipeline_revenue')">
      <template #append>
        <Icon name="mdi:euro" class="text-xl absolute right-2 text-gray-400 group-focus-within:right-10 group-hover:right-10" />
      </template>
    </b-text-field>
    <b-chip-input searchable :items="skillsItems" v-model="selectedSkills" :label="t('modal_create_mission_step3.required_skills')" :max-items="5" data-e2e="experience-skills-input" />
    <b-star-fieldset v-model="description" context="freelance" allow-free-text :hint="t('star_fieldset.optional_recommended')" data-e2e="experience-description-star" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, toRefs } from 'vue';
import BTextField from "~/components/forms/BTextField.vue";
import BStarFieldset from "~/components/forms/BStarFieldset.vue";
import BChipInput from "~/components/forms/chip-input/BChipInput.vue";
import {groupByCategory} from "~/utils/categoryGrouping";
import type { Experience } from "~/api/v2/experiences/ExperienceRepository";
const { t } = useI18n();

interface Props {
  experience?: Experience;
  initialData?: Record<string, any>;
}

const props = defineProps<Props>()
const { initialData } = toRefs(props)

const emit = defineEmits(['change'])

const skillStore = useSkillStore()

const { skills } = storeToRefs(skillStore)

const title = ref('')
const pipelineRevenue = ref('')
const description = ref('')
const selectedSkills = ref([])

// Flag to prevent emitting during draft restoration
const isRestoringData = ref(false)

const skillsItems = computed(() => groupByCategory(skills.value || []))

watch(() => props.experience, (experience) => {
  if (experience) {
    title.value = experience.title || ''
    description.value = experience.description || ''
    selectedSkills.value = (experience.skills || []).map((skill) => ({ value: skill.skill_id, label: skill.name }))
    pipelineRevenue.value = parseFloat(experience.pipeline_revenue)
  }
}, { immediate: true })

// Watch for draft restoration
watch(initialData, (data) => {
  if (data && Object.keys(data).length > 0) {
    isRestoringData.value = true
    if (data.name) {
      title.value = data.name
    }
    if (data.description) {
      description.value = data.description
    }
    if (data.pipeline_revenue) {
      pipelineRevenue.value = data.pipeline_revenue
    }
    if (data.skill_ids && data.skill_ids.length > 0) {
      if (data.skill_labels && data.skill_labels.length > 0) {
        selectedSkills.value = [...data.skill_labels]
      } else {
        // Fallback: use IDs as labels
        selectedSkills.value = data.skill_ids.map(id => ({ value: id, label: id }))
      }
    }
    // Reset flag after Vue processes all updates
    nextTick(() => {
      isRestoringData.value = false
    })
  }
}, { immediate: true, deep: true })

onMounted(() => {
  skillStore.fetchSkills()
})

watch([
  selectedSkills,
  title,
  pipelineRevenue,
  description,
], () => {
  // Skip emitting during draft restoration to prevent infinite loops
  if (isRestoringData.value) return

  emit('change', {
    name: title.value,
    description: description.value,
    skill_ids: selectedSkills.value.map((skill) => skill.value),
    skill_labels: selectedSkills.value.map((skill) => ({ value: skill.value, label: skill.label })),
    pipeline_revenue: parseFloat(pipelineRevenue.value),
  })
}, { deep: true })
</script>
